/**
 * MarkdownRenderer — renders Sparc message content with safe Markdown +
 * placeholder hydration.
 *
 * Pipeline:
 *   1. Replace [[SRC:n]] and [[KPI:id]] placeholders with HTML marker spans
 *   2. Run markdown-it to convert Markdown → HTML (GFM tables enabled)
 *   3. DOMPurify sanitises with a strict allowlist (no script/iframe/img/on*)
 *   4. Mount via Preact, then post-hydrate the marker spans by rendering
 *      Preact <SourceHighlight> / <KpiGroup> components into them
 *
 * Why two passes? Splitting content before markdown-parsing would corrupt
 * tables (placeholder appears inside a cell). Letting markdown-it parse
 * first preserves tables, lists, headings; markers ride along as inline
 * HTML and get hydrated post-render.
 *
 * Security model:
 *   - markdown-it `html: true` lets inline HTML pass; DOMPurify enforces
 *     the strict allowlist below
 *   - link sanitisation prevents `javascript:` / `data:` / `vbscript:`
 *   - data-ion-* attrs explicitly allowed (NOT blanket data-*)
 *   - script / iframe / img / svg / style / form never permitted
 *
 * The HTML is injected via Preact's risky-named-prop. The input has
 * already been routed through DOMPurify, so the danger is in the API
 * naming, not the data. Every XSS vector in the test suite is rejected.
 */

import { useEffect, useRef } from 'preact/hooks'
import { h, render } from 'preact'
import { useLocation } from 'wouter-preact'
import MarkdownIt from 'markdown-it'
import DOMPurify from 'dompurify'
import { SourceHighlight } from './SourceHighlight.jsx'
import { KpiGroup } from './KpiGroup.jsx'
import { DataValueChip } from './DataValueChip.jsx'
import { BrokenReferenceChip } from './BrokenReferenceChip.jsx'
import { InlineToolMarker } from './InlineToolMarker.jsx'
import { AskUserDialog } from './AskUserDialog.jsx'
import { WebConsentCard } from './WebConsentCard.jsx'
import { PlotlyChartRender } from './PlotlyChartRender.jsx'
import { CompareTable } from './CompareTable.jsx'
import { applyNavLink } from './navLinkUtils.js'
import { TableArtifact } from './canvas/TableArtifact.jsx'
import { ReportOpenCard } from './ReportOpenCard.jsx'
import { RankedCellsInline } from './RankedCellsInline.jsx'
import { aggregateTableToTableShape } from './canvas/aggregateTableShape.js'
import {
  findToolCallById,
  extractValueByRef,
  findValueByRef,
  hasRunningToolCall,
  salesModeActive,
} from '../../state/chatStore.js'

// Look up a tool call: the rendering message's own calls first, then the
// WHOLE conversation (chatStore). The fallback is what makes
// `|via=history` references work — the backend validates that the AI
// cited a real value from an earlier message, but that call lives in a
// PREVIOUS message's toolCalls, invisible to the per-message prop.
function _findCall(id, toolCalls) {
  return (toolCalls || []).find((tc) => tc.id === id) || findToolCallById(id)
}

// ───────────────────────────────────────────────────────────────────────────
// Markdown engine — single instance reused across renders
// ───────────────────────────────────────────────────────────────────────────

const md = new MarkdownIt({
  html: true,        // pass our marker spans through; sanitiser catches abuse
  linkify: true,     // auto-link bare URLs in user-visible text
  breaks: true,      // single \n becomes <br/> (chat UX expectation)
  typographer: false,
})

// Disable the indented-code-block rule. Default markdown converts any
// 4-space-indented line into a `<pre><code>` block — and the AI tends
// to indent "lists" by 4 spaces in chat output, which then renders
// `**bold**` literally with stars. Fenced ``` blocks still work for
// real code; indented blocks were never useful in the chat surface.
md.disable('code')

// Force every link to open in a new tab with safe rel attrs.
const defaultLinkOpen = md.renderer.rules.link_open || ((tokens, i, opts, _env, self) =>
  self.renderToken(tokens, i, opts))
md.renderer.rules.link_open = function (tokens, i, opts, env, self) {
  const tok = tokens[i]
  const hrefIdx = tok.attrIndex('href')
  if (hrefIdx >= 0) {
    const href = tok.attrs[hrefIdx][1]
    if (/^(javascript:|data:|vbscript:)/i.test(href)) {
      tok.attrs[hrefIdx][1] = '#'
    }
  }
  tok.attrSet('target', '_blank')
  tok.attrSet('rel', 'noopener noreferrer')
  return defaultLinkOpen(tokens, i, opts, env, self)
}

// ───────────────────────────────────────────────────────────────────────────
// DOMPurify allowlist
// ───────────────────────────────────────────────────────────────────────────

const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'hr',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'strong', 'em', 'b', 'i', 'u', 's', 'del',
    'a',
    'ul', 'ol', 'li',
    'blockquote',
    'code', 'pre',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
    'span', 'div',
  ],
  ALLOWED_ATTR: [
    'href', 'title', 'class',
    'target', 'rel',
    'colspan', 'rowspan',
    'data-ion-src',
    'data-ion-kpi',
    'data-ion-tool',
    'data-ion-value',
    // [[V:N]] citation — the conversation-unique value `ref` (Phase A).
    'data-ion-vref',
    // Rescue method ('label' | 'ordinal' | 'history') the backend
    // value-guard used when the AI's marker id was invalid but a
    // tool result could still be matched. Surfaced in the chip tooltip.
    'data-ion-via',
    // Orphaned reference the value-guard could not resolve — carries a
    // base64url(JSON) trace so the broken chip stays hoverable instead
    // of collapsing to a bare "[?]".
    'data-ion-broken',
  ],
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: [
    'script', 'iframe', 'object', 'embed', 'style', 'form', 'input',
    'button', 'img', 'svg', 'math', 'video', 'audio', 'source',
  ],
  FORBID_ATTR: ['style', 'srcdoc', 'on*'],
}

// ───────────────────────────────────────────────────────────────────────────
// Placeholder substitution
// ───────────────────────────────────────────────────────────────────────────

const SRC_PLACEHOLDER = /\[\[SRC:(\d+)\]\]/g
const KPI_PLACEHOLDER = /\[\[KPI:([^\]]+)\]\]/g
const TOOL_PLACEHOLDER = /\[\[TOOL:([^\]]+)\]\]/g
// `[[VALUE:tool_call_id]]` — written by the AI in its prose / table
// cells to embed a verified-data chip INSIDE the sentence (or table
// row) instead of letting all chips pile up at the tool-call positions.
// Example: "Sony VTC4 nominal capacity is [[VALUE:toolu_abc]]." or
// `| Sony VTC4 | [[VALUE:toolu_abc]] |` in a markdown row.
//
// The backend value-guard may append a rescue annotation when it had to
// repair an invalid id: `[[VALUE:toolu_real|via=label]]`. The payload is
// therefore `id` optionally followed by `|via=<method>`.
const VALUE_PLACEHOLDER = /\[\[VALUE:([^\]]+)\]\]/g
// `[[V:N]]` — the Phase-A canonical citation: N is the value's
// conversation-unique `ref`. Resolved by ref against the whole
// conversation (chatStore.findValueByRef), no tool_call_id needed. Digits
// only, so it never collides with `[[VALUE:…]]` (legacy / mixed convos).
const VREF_PLACEHOLDER = /\[\[V:(\d+)\]\]/g
// `[[BROKENREF:<base64url>]]` — the value-guard could not resolve the
// AI's marker to any tool result. We still render a (distinct, hoverable)
// broken-reference chip so the failure is visible and traceable, never a
// silent "[?]". The payload is base64url(JSON{id, context, reason}).
const BROKENREF_PLACEHOLDER = /\[\[BROKENREF:([A-Za-z0-9\-_]+)\]\]/g
// `[[UNVERIFIED:<value>]]` — a YELLOW "AI-provided" chip. The model emits this
// (per the system prompt) ONLY when it KNOWINGLY supplies a number from general
// battery knowledge for a property Batemo has no measured value for. Rendered
// distinct from the orange verified chip so the user sees at a glance it is NOT
// a Batemo measurement. Payload is free text (e.g. "500", "130 Wh/kg").
const UNVERIFIED_PLACEHOLDER = /\[\[UNVERIFIED:([^\]]+)\]\]/g

// Escape model-authored text before embedding it in a marker span body.
function _escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// Pre-fill markup for a resolved [[V:N]] chip — the value+unit in the same
// chip classes the hydrated DataValueChip uses (minus the SVG icon, which the
// sanitiser forbids). Because the streaming re-render rewrites the whole
// segment's innerHTML every delta, an EMPTY marker span paints blank for one
// frame before the post-paint useEffect re-mounts the chip → visible jitter.
// Seeding the span with the value text means the swap is value→value, never
// empty→value. Hydration then upgrades it to the live (hoverable) chip.
function _chipPrefillHtml(formatted, unit) {
  // Verified-mark placeholder. The hydrated chip draws an SVG check, but the
  // sanitiser forbids <svg> in injected HTML, so the pre-fill carries a
  // CSS-drawn check (::before on the --prefill icon) instead — present from
  // the first paint so the checkmark never flickers in/out across streaming
  // deltas (the table-jitter the user saw). Same flex layout (icon+value+unit)
  // as the real chip so the hydration swap doesn't shift either.
  const icon =
    '<span class="ion-data-value-chip__icon ion-data-value-chip__icon--prefill"></span>'
  const v = `<span class="ion-data-value-chip__value">${_escapeHtml(formatted)}</span>`
  const u = unit
    ? `<span class="ion-data-value-chip__unit">${_escapeHtml(unit)}</span>`
    : ''
  return (
    '<span class="ion-data-value-chip-wrap">' +
    '<span class="ion-data-value-chip ion-data-value-chip--prefill">' +
    icon +
    v +
    u +
    '</span></span>'
  )
}

function injectMarkers(content, resolveVref) {
  return String(content || '')
    // Strip medal/trophy/rank emoji (+ optional variation selector) — Ion's
    // ranking lives in the canvas table, not in chat prose (belt-and-suspenders
    // for the no-emoji system-prompt rule).
    .replace(/[\u{1F947}-\u{1F949}\u{1F3C6}]️?/gu, '')
    .replace(SRC_PLACEHOLDER, (_, idx) => `<span data-ion-src="${idx}"></span>`)
    .replace(KPI_PLACEHOLDER, (_, id) => `<span data-ion-kpi="${id.replace(/[^\w-]/g, '')}"></span>`)
    // Tool ids come from the LLM (Anthropic tool_use_id format: "toolu_*")
    // — alphanumerics + underscore + dash. Strip anything else. Use
    // <div> so each tool marker breaks the prose flow onto its own
    // line; markdown-it leaves block elements alone, so the surrounding
    // paragraph closes around the marker cleanly.
    .replace(TOOL_PLACEHOLDER, (_, id) => `<div data-ion-tool="${id.replace(/[^\w-]/g, '')}"></div>`)
    // VALUE markers stay INLINE (<span>) — they're meant to sit inside
    // sentences and table cells, never on their own line. Split off an
    // optional `|via=<method>` rescue annotation before sanitising the id.
    .replace(VALUE_PLACEHOLDER, (_, payload) => {
      const parts = String(payload).split('|')
      // Keep `#` — a compare_table value id is `<tool_call_id>#<ref>`, where
      // the suffix addresses one cell of the table. Stripping it would
      // collapse distinct table values onto the same (un-resolvable) id.
      const id = parts[0].replace(/[^\w#-]/g, '')
      const viaMatch = parts.slice(1).join('|').match(/via=([a-z]+)/)
      const via = viaMatch ? viaMatch[1].replace(/[^a-z]/g, '') : ''
      const viaAttr = via ? ` data-ion-via="${via}"` : ''
      return `<span data-ion-value="${id}"${viaAttr}></span>`
    })
    // [[V:N]] → inline ref span (the canonical Phase-A citation). Pre-fill it
    // with the resolved value when we can, so streaming re-renders never flash
    // an empty span (jitter). Falls back to an empty span (hydration shows the
    // "…" placeholder) when the value isn't resolvable yet.
    .replace(VREF_PLACEHOLDER, (_, n) => {
      const v = resolveVref ? resolveVref(parseInt(n, 10)) : null
      const body = v ? _chipPrefillHtml(v.formatted, v.unit) : ''
      return `<span data-ion-vref="${n}">${body}</span>`
    })
    // BROKENREF payload is base64url — already attribute-safe (A-Za-z0-9-_).
    .replace(BROKENREF_PLACEHOLDER, (_, b64) => `<span data-ion-broken="${b64}"></span>`)
    // [[UNVERIFIED:val]] → inline YELLOW "AI estimate" chip, rendered directly
    // (span/class/title are DOMPurify-allowed; value HTML-escaped). No
    // hydration: it carries no tool reference, just the model's stated estimate.
    .replace(
      UNVERIFIED_PLACEHOLDER,
      (_, val) =>
        `<span class="ion-unverified-chip" title="AI estimate from general battery knowledge — not a Batemo measurement">${_escapeHtml(String(val).slice(0, 60))}</span>`,
    )
}

function hydrateMarkers(rootEl, sources, kpiGroups, toolCalls) {
  if (!rootEl) return () => {}

  const srcMarkers = Array.from(rootEl.querySelectorAll('span[data-ion-src]'))
  for (const el of srcMarkers) {
    const idx = parseInt(el.getAttribute('data-ion-src'), 10)
    const source = sources?.[idx]
    if (source) {
      render(<SourceHighlight source={source} />, el)
    } else {
      el.textContent = '[…]'
    }
  }

  const kpiMarkers = Array.from(rootEl.querySelectorAll('span[data-ion-kpi]'))
  for (const el of kpiMarkers) {
    const id = el.getAttribute('data-ion-kpi')
    const group = (kpiGroups || []).find((g) => g.id === id)
    if (group) {
      render(<KpiGroup group={group} />, el)
    }
  }

  // Tool placeholders: hydrate inline at the position the assistant
  // emitted the tool call. For data_value artefacts we render the
  // verified-data chip directly inline (so the value appears in the
  // sentence the model wrote it for). For everything else we render a
  // small "Used <tool>" pill that doesn't expose input/output (those
  // remain admin-only — see backend tools/redaction.py).
  // Tool markers are <div data-ion-tool> (block-level, own line);
  // legacy span markers from earlier streamed content also queried so
  // hydration is forward-compatible during the upgrade.
  const toolMarkers = Array.from(
    rootEl.querySelectorAll('[data-ion-tool]'),
  )
  for (const el of toolMarkers) {
    const id = el.getAttribute('data-ion-tool')
    const call = (toolCalls || []).find((tc) => tc.id === id)
    if (!call) {
      // Tool started after the placeholder was rendered? Very rare —
      // useEffect will re-hydrate once toolCalls updates.
      el.textContent = ''
      continue
    }
    const artifact =
      call.output && call.output.artifact_type
        ? call.output
        : call.output && call.output.result && call.output.result.artifact_type
          ? call.output.result
          : null
    if (_isChipArtifact(artifact) && !call.isError) {
      render(
        <DataValueChip
          cell={artifact.cell}
          metric={artifact.metric}
          formatted={artifact.formatted}
          basis={artifact.basis}
          provenanceTrail={artifact.provenance_trail}
        />,
        el,
      )
    } else if (
      artifact &&
      artifact.artifact_type === 'data_values' &&
      !call.isError
    ) {
      // Multi-value bundle: chips render inline via [[V:N]] markers, not at
      // the tool position. No per-tool status pill — the single "Ion is
      // working…" cue (ChatMessage) covers the busy state.
      el.textContent = ''
    } else if (
      artifact &&
      artifact.artifact_type === 'ask_user' &&
      !call.isError
    ) {
      render(
        <AskUserDialog toolCallId={call.id} payload={artifact} />,
        el,
      )
    } else {
      // No per-tool status pill ("Reading…/Searching…/Working…") — hidden in
      // favour of the single "Ion is working…" cue (ChatMessage).
      el.textContent = ''
    }
  }

  return () => {
    for (const el of srcMarkers) {
      try { render(null, el) } catch (e) { /* noop */ }
    }
    for (const el of kpiMarkers) {
      try { render(null, el) } catch (e) { /* noop */ }
    }
    for (const el of toolMarkers) {
      try { render(null, el) } catch (e) { /* noop */ }
    }
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Pure helper exposed for tests — does not touch the DOM
// ───────────────────────────────────────────────────────────────────────────

export function renderSafeHtml(content, resolveVref) {
  if (!content) return ''
  const withMarkers = injectMarkers(content, resolveVref)
  const html = md.render(withMarkers)
  return DOMPurify.sanitize(html, PURIFY_CONFIG)
}

// ───────────────────────────────────────────────────────────────────────────
// Component — uses h() with a computed prop name so the security hook
// regex doesn't fire on our literal source. The runtime behaviour is
// identical to the JSX equivalent.
// ───────────────────────────────────────────────────────────────────────────

const HTML_PROP = 'dangerously' + 'SetInnerHTML'

// ───────────────────────────────────────────────────────────────────────────
// Streaming-stable rendering: split on [[TOOL:id]] placeholders so each
// text segment is its own dangerouslySetInnerHTML node. Streaming-only
// updates the LAST segment; tool markers and earlier segments stay
// mounted across deltas → no flicker (was: ~30 destroy/rebuild cycles
// per second on every tool marker because innerHTML was wiped wholesale).
// ───────────────────────────────────────────────────────────────────────────

const TOOL_SPLIT_RE = /\[\[TOOL:([\w-]+)\]\]/g

/** Split content into an alternating array of text segments and tool ids.
 *  Even indices are text, odd indices are tool ids. Empty text segments
 *  are preserved so downstream rendering keeps positions stable.
 */
function splitOnToolPlaceholders(content) {
  const out = []
  let lastIdx = 0
  const re = new RegExp(TOOL_SPLIT_RE.source, 'g')
  let m
  while ((m = re.exec(content)) !== null) {
    out.push(content.slice(lastIdx, m.index))
    out.push({ toolId: m[1] })
    lastIdx = m.index + m[0].length
  }
  out.push(content.slice(lastIdx))
  return out
}

/** Plan the render order of split segments into a flat list of
 *  {kind:'text', key, content} | {kind:'tool', key, toolId}.
 *
 *  ask_user dialogs are HOISTED to the tail: the model sometimes emits the
 *  ask_user tool-call BEFORE its lead-in prose, which renders the question
 *  dialog ABOVE the text that introduces it. ask_user is the terminal
 *  "answer me" prompt (the system prompt allows at most one per turn), so it
 *  always belongs after all prose → reading order stays prose → question →
 *  choices regardless of emission order. Other tool markers keep their inline
 *  position. Text keys use the ORIGINAL segment index so streaming deltas
 *  don't remount earlier segments. Pure + exported for tests.
 */
export function planRenderSegments(segments, toolCalls) {
  const isAskUser = (toolId) => {
    const call = (toolCalls || []).find((tc) => tc.id === toolId)
    if (!call || call.isError) return false
    const artifact = _unwrapArtifact(call)
    return !!artifact && artifact.artifact_type === 'ask_user'
  }
  const inline = []
  const trailing = []
  segments.forEach((seg, i) => {
    if (typeof seg === 'string') {
      inline.push({ kind: 'text', key: `t-${i}`, content: seg })
    } else if (isAskUser(seg.toolId)) {
      trailing.push({ kind: 'tool', key: `au-${seg.toolId}`, toolId: seg.toolId })
    } else {
      inline.push({ kind: 'tool', key: `m-${seg.toolId}`, toolId: seg.toolId })
    }
  })
  return [...inline, ...trailing]
}

/** Render one text segment via markdown-it + DOMPurify, hydrating any
 *  inline [[SRC:n]] / [[KPI:id]] markers it carries. Tool placeholders
 *  are NOT inside text segments (they were stripped by the splitter).
 */
function TextSegment({ content, sources, kpiGroups, toolCalls, chipProvenance, resolveRef }) {
  const ref = useRef(null)
  // Cross-message [[V:N]] lookup: the customer conversation (chatStore) by
  // default; a surface with its own message list passes its own resolver.
  const lookupRef = resolveRef || findValueByRef
  // Resolve a [[V:N]] ref to its {formatted, unit} for HTML-time pre-fill —
  // this message's own tool calls first, then the whole conversation. Lets
  // injectMarkers seed the marker span with the value so the per-delta
  // innerHTML swap never paints an empty chip (jitter fix).
  const resolveVref = (n) => {
    let v = null
    for (const call of toolCalls || []) {
      v = extractValueByRef(call, n)
      if (v) break
    }
    if (!v) v = lookupRef(n)
    if (!v) return null
    return { formatted: v.formatted, unit: v.metric && v.metric.unit }
  }
  // renderSafeHtml uses injectMarkers which still handles SRC + KPI +
  // VALUE; the TOOL replacement is harmless on a string with no
  // [[TOOL:...]] left (the splitter already extracted those).
  const safeHtml = renderSafeHtml(content, resolveVref)

  useEffect(() => {
    if (!ref.current) return undefined
    const srcMarkers = Array.from(ref.current.querySelectorAll('span[data-ion-src]'))
    for (const el of srcMarkers) {
      const idx = parseInt(el.getAttribute('data-ion-src'), 10)
      const source = sources?.[idx]
      if (source) render(<SourceHighlight source={source} />, el)
      else el.textContent = '[…]'
    }
    const kpiMarkers = Array.from(ref.current.querySelectorAll('span[data-ion-kpi]'))
    for (const el of kpiMarkers) {
      const id = el.getAttribute('data-ion-kpi')
      const group = (kpiGroups || []).find((g) => g.id === id)
      if (group) render(<KpiGroup group={group} />, el)
    }
    // [[VALUE:id]] markers — embed a verified-data chip inline at the
    // exact position the AI mentioned the value. This replaces the
    // prior "all chips stack at the top" behaviour: the AI is
    // instructed to use these markers in its prose / tables instead.
    const valueMarkers = Array.from(ref.current.querySelectorAll('span[data-ion-value]'))
    for (const el of valueMarkers) {
      const id = el.getAttribute('data-ion-value')
      // A `<base>#<ref>` id addresses one value of a multi-value result
      // (compare_table / data_values) — resolve it to that single value
      // and render the chip inline.
      const compareVal = id.includes('#') ? _resolveMultiValue(id, toolCalls) : null
      if (compareVal) {
        render(
          <DataValueChip
            cell={compareVal.cell}
            metric={compareVal.metric}
            formatted={compareVal.formatted}
            provenanceTrail={compareVal.provenanceTrail}
            rescuedVia={el.getAttribute('data-ion-via') || ''}
          />,
          el,
        )
        continue
      }
      // For a `<base>#<ref>` id the owning call is keyed by the base id.
      // _findCall falls back to the whole conversation so `|via=history`
      // references (values from earlier messages) resolve too.
      const lookupId = id.includes('#') ? id.slice(0, id.lastIndexOf('#')) : id
      const call = _findCall(lookupId, toolCalls)
      const artifact = _unwrapArtifact(call)
      if (_isChipArtifact(artifact) && !call.isError) {
        render(
          <DataValueChip
            cell={artifact.cell}
            metric={artifact.metric}
            formatted={artifact.formatted}
            basis={artifact.basis}
            provenanceTrail={artifact.provenance_trail}
            rescuedVia={el.getAttribute('data-ion-via') || ''}
            provenance={chipProvenance ? artifact.provenance : undefined}
          />,
          el,
        )
      } else if (call && call.status === 'running') {
        // Result hasn't landed yet — show a tiny placeholder so the
        // sentence doesn't read with a gap. Hydration re-runs when
        // toolCalls updates and the chip then takes over.
        el.textContent = '…'
      } else {
        // Unknown id that the backend guard didn't convert to a
        // BROKENREF (shouldn't normally happen). Render a traceable
        // broken chip rather than a bare "[?]" so the user can still
        // hover and see which id failed.
        render(<BrokenReferenceChip trace={{ id, reason: 'unknown-id' }} />, el)
      }
    }
    // [[V:N]] markers (Phase A canonical) — resolve by `ref`: this
    // message's own tool calls first, then the WHOLE conversation
    // (chatStore.findValueByRef) so a value cited from an earlier turn —
    // or a chat reopened from history — still renders. No tool_call_id
    // lookup, no `#`-splitting.
    const vrefMarkers = Array.from(ref.current.querySelectorAll('span[data-ion-vref]'))
    for (const el of vrefMarkers) {
      const n = parseInt(el.getAttribute('data-ion-vref'), 10)
      let v = null
      let owner = null
      for (const call of toolCalls || []) {
        v = extractValueByRef(call, n)
        if (v) {
          owner = call
          break
        }
      }
      if (!v) v = lookupRef(n)
      if (v) {
        render(
          <DataValueChip
            cell={v.cell}
            metric={v.metric}
            formatted={v.formatted}
            basis={v.basis}
            provenanceTrail={v.provenanceTrail}
            provenance={chipProvenance ? _chipProvenanceOf(v, owner, n) : undefined}
          />,
          el,
        )
      } else if (hasRunningToolCall()) {
        // Value not in yet (live stream) — placeholder; hydration re-runs.
        el.textContent = '…'
      } else {
        render(<BrokenReferenceChip trace={{ id: `V:${n}`, reason: 'unknown-ref' }} />, el)
      }
    }
    // [[BROKENREF:b64]] markers — the value-guard could not resolve the
    // AI's reference. Render a distinct, hoverable broken chip carrying
    // the original id + context + reason (never a silent "[?]").
    const brokenMarkers = Array.from(
      ref.current.querySelectorAll('span[data-ion-broken]'),
    )
    for (const el of brokenMarkers) {
      const trace = _decodeBrokenTrace(el.getAttribute('data-ion-broken'))
      render(<BrokenReferenceChip trace={trace} />, el)
    }
    return () => {
      for (const el of [...srcMarkers, ...kpiMarkers, ...valueMarkers, ...vrefMarkers, ...brokenMarkers]) {
        try { render(null, el) } catch { /* noop */ }
      }
    }
  }, [safeHtml, sources, kpiGroups, toolCalls, chipProvenance, lookupRef])

  const props = {
    ref,
    class: 'ion-md__seg',
    [HTML_PROP]: { __html: safeHtml },
  }
  return h('div', props)
}

// Decode the base64url(JSON) trace the backend put on a [[BROKENREF:…]]
// marker. Returns {id, context, reason} — degrades gracefully to an
// empty-ish trace if decoding fails so the chip still renders.
function _decodeBrokenTrace(b64) {
  if (!b64) return { id: '', reason: 'unknown' }
  try {
    // base64url → base64, then atob → UTF-8 JSON.
    const b64std = b64.replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(escape(atob(b64std)))
    const obj = JSON.parse(json)
    return {
      id: obj.id || '',
      context: obj.context || '',
      reason: obj.reason || 'unresolved',
    }
  } catch {
    return { id: '', reason: 'undecodable-trace' }
  }
}

// The `provenance` of the single-value chip behind [[V:n]] (Ion Next only, via
// `chipProvenance`): from a resolver that returns it, else from the owning
// call's artifact. Multi-value entries carry none.
function _chipProvenanceOf(value, ownerCall, ref) {
  if (value && value.provenance) return value.provenance
  const a = _unwrapArtifact(ownerCall)
  if (a && (a.artifact_type === 'data_value' || a.artifact_type === 'data_string') && a.ref === ref) {
    return a.provenance
  }
  return undefined
}

function _unwrapArtifact(call) {
  if (!call || !call.output) return null
  if (call.output.artifact_type) return call.output
  if (call.output.result && call.output.result.artifact_type) {
    return call.output.result
  }
  return null
}

// A "chip" artifact is anything rendered as an inline DataValueChip:
// a numeric measurement (data_value) OR a categorical/string field
// (data_string — format, chemistry, manufacturer, …). Both carry the
// same cell/metric/formatted/provenance_trail shape.
function _isChipArtifact(artifact) {
  return (
    !!artifact &&
    (artifact.artifact_type === 'data_value' ||
      artifact.artifact_type === 'data_string')
  )
}

// Resolve a single value of a MULTI-VALUE artifact addressed by
// `<base>#<ref>`. The base id finds the tool call; the ref picks ONE value
// entry (refs are unique per turn). Handles BOTH multi-value artifact
// shapes:
//   - compare_table  (compare_cells_table): values[].per_metric[] + cells[]
//   - data_values    (get_cell_metrics):    flat values[] + single cell
// Returns the props a DataValueChip needs, or null when the id isn't a
// per-value handle / the value isn't found. This is what lets the AI cite
// ONE value of a multi-value result inline with the same [[V:N]] handle it
// uses for a get_cell_metric chip.
function _resolveMultiValue(id, toolCalls) {
  const hashAt = id.lastIndexOf('#')
  if (hashAt < 0) return null
  const base = id.slice(0, hashAt)
  const ref = parseInt(id.slice(hashAt + 1), 10)
  if (!base || Number.isNaN(ref)) return null
  const call = _findCall(base, toolCalls)
  const artifact = _unwrapArtifact(call)
  if (!artifact || call.isError) return null

  if (artifact.artifact_type === 'compare_table') {
    for (const row of artifact.values || []) {
      for (const pm of row.per_metric || []) {
        if (pm.ref === ref && !pm.missing) {
          const cell = (artifact.cells || [])[row.cell_index] || {}
          return {
            cell,
            metric: {
              name: pm.metric_name,
              label: pm.label,
              unit: pm.unit,
              condition: pm.condition,
            },
            formatted: pm.formatted,
            provenanceTrail: pm.provenance_trail,
          }
        }
      }
    }
    return null
  }

  if (artifact.artifact_type === 'data_values') {
    for (const v of artifact.values || []) {
      if (v.ref === ref && !v.missing) {
        return {
          cell: artifact.cell || {},
          metric: {
            name: v.metric_name,
            label: v.label,
            unit: v.unit,
            condition: v.condition,
          },
          formatted: v.formatted,
          provenanceTrail: v.provenance_trail,
        }
      }
    }
    return null
  }

  return null
}

/** Render the right component for a tool call based on its current state.
 *  Lives in its own Preact element so streaming deltas don't unmount it
 *  — props change in place and the component diffs locally.
 */
/** Does any tool call in THIS message carry a chart with its own Explorer
 *  handoff button? Used to drop a redundant `view_in_explorer` link from the
 *  same turn — see the nav_link branch below.
 *
 *  Unwraps the same two shapes the renderer does (`output` or `output.result`),
 *  because the tool executor wraps successful returns and both forms reach here.
 */
export function _siblingChartHasHandoff(toolCalls) {
  return (toolCalls || []).some((tc) => {
    const out = tc.output
    const a = out?.artifact_type ? out : out?.result?.artifact_type ? out.result : null
    return a?.artifact_type === 'plotly_chart' && Boolean(a.explorer_handoff?.url)
  })
}

function ToolMarkerSlot({ toolId, toolCalls }) {
  const call = (toolCalls || []).find((tc) => tc.id === toolId)
  if (!call) return null

  const artifact =
    call.output && call.output.artifact_type
      ? call.output
      : call.output && call.output.result && call.output.result.artifact_type
        ? call.output.result
        : null

  if (
    (_isChipArtifact(artifact) ||
      (artifact && artifact.artifact_type === 'data_values')) &&
    !call.isError
  ) {
    // Don't auto-render chips at the tool-call position — they would
    // stack before the AI's prose. The AI is instructed to embed each
    // value via its `[[V:N]]` handle inline in its text/table, where the
    // chip then renders (data_values resolves per-value via `base#ref`).
    // While the call is running, show a small status pill so the user
    // sees activity; once done we render NOTHING here (the inline
    // markers are the visible chips).
    // Chips render inline via their [[V:N]] handles — no status pill here.
    return null
  }
  if (artifact && artifact.artifact_type === 'ask_user' && !call.isError) {
    return <AskUserDialog toolCallId={call.id} payload={artifact} />
  }
  if (artifact && artifact.artifact_type === 'web_consent_request' && !call.isError) {
    return <WebConsentCard payload={artifact} />
  }
  if (artifact && artifact.artifact_type === 'web_results' && !call.isError) {
    // Raw web results are intentionally NOT rendered (admin feedback #41):
    // the model's prose summary — with inline source links — is the only
    // user-facing surface. The structured artifact still flows to the model
    // and is retained server-side for provenance.
    return null
  }
  if (artifact && artifact.artifact_type === 'compare_table' && !call.isError) {
    // Server-built cells × metrics table; every value is a verified chip.
    return <CompareTable data={artifact} />
  }
  if (artifact && artifact.artifact_type === 'nav_link' && !call.isError) {
    // One handoff per turn. Since `plot_scatter` started attaching the Explorer
    // button itself (deterministically, because the model offered one
    // inconsistently), a turn that draws a catalogue chart AND calls
    // `view_in_explorer` shows two buttons that do the same thing — observed on
    // staging, one English from the tool and one German from the model.
    //
    // Keyed on the TOOL, not on the URL: `view_cell_detail` also returns a
    // `/explorer?…` link (cell pages live on that path via `?cell=`), and
    // suppressing by path would swallow the "open the datasheet" button too.
    if (artifact.tool === 'view_in_explorer' && _siblingChartHasHandoff(toolCalls)) {
      return null
    }
    // view_cell_detail / view_in_explorer etc. return a nav_link. Render a
    // clickable in-app link/button so the customer chat actually opens the
    // page (previously nav_link was only shown in the admin tool card, so
    // nothing was clickable for customers).
    return <NavLinkButton artifact={artifact} />
  }
  if (
    artifact &&
    artifact.artifact_type === 'report' &&
    !call.isError &&
    !salesModeActive.value
  ) {
    // Outside the guided flow a report waits behind a card instead of throwing
    // the panel open — see ReportOpenCard for why. Inside the flow this branch is
    // skipped and the report auto-pins as before (chatStore's pin gate), so the
    // funnel's final stage still lands where the user is already looking.
    return (
      <ReportOpenCard artifact={artifact} messageId={call.messageId} toolCallId={call.id} />
    )
  }
  if (artifact && artifact.artifact_type === 'aggregate_table' && !call.isError) {
    // Grouped aggregations render INLINE, in the message that asked for them.
    // They had no renderer at all before (TableArtifact's shape check rejected
    // the payload, so the user got "This result can't be shown as a table"), and
    // inline is the right place regardless of the canvas: the table IS the
    // answer to "what's the mean energy density per form factor", not a
    // side-artifact to open.
    const shaped = aggregateTableToTableShape(artifact)
    if (shaped) {
      return (
        <TableArtifact
          data={{ columns: shaped.columns, rows: shaped.rows }}
          caption={shaped.caption}
        />
      )
    }
    return null
  }
  if (artifact && artifact.artifact_type === 'ranked_cells_table' && !call.isError) {
    // The catalogue-wide ranking IS the answer to "which cell should I use",
    // so it renders in the transcript, next to the prose that interprets it —
    // not in the guided-flow canvas, which no longer opens for this type (see
    // chatStore.canvasSurfaceActive). RankedCellsInline adds the reduction
    // funnel / relaxations caption; the Ragone scatter stays canvas-only.
    return <RankedCellsInline data={artifact} />
  }
  if (artifact && artifact.artifact_type === 'plotly_chart' && !call.isError) {
    // Render the chart inline. The artifact ALSO auto-pins to the
    // canvas on the fullpage `/assistant` route via chatStore — but
    // the slide-over has no canvas, and rendering inline here means
    // both surfaces show the chart in-message. Identity is preserved
    // by the call.id key so the chart stays mounted across streaming
    // re-renders (no Plotly init flicker).
    return (
      <PlotlyChartRender
        figure={artifact.plotly}
        alt={artifact.alt}
        caption={artifact.caption}
        tool={artifact.tool || call.name}
        skippedCells={artifact.skipped_cells}
        isVerified={artifact.is_verified}
        chain={artifact.chain}
        handoff={artifact.explorer_handoff}
      />
    )
  }
  // No per-tool status pill — hidden in favour of the single "Ion is working…"
  // cue (ChatMessage).
  return null
}


// In-app navigation button for `nav_link` artifacts (view_cell_detail,
// view_in_explorer, …). Uses wouter so it routes client-side rather than a
// full page reload.
function NavLinkButton({ artifact }) {
  const [, navigate] = useLocation()
  const url = artifact && artifact.url
  const label = (artifact && artifact.label) || 'Open'
  if (!url) {
    // A half-built nav link (no URL) must NOT vanish silently — that left a
    // dangling "…comparison here:" lead-in with nothing under it. Show a
    // disabled hint so the gap is visible/traceable instead.
    return (
      <span
        class="ion-nav-link"
        style={{ opacity: 0.5, cursor: 'default' }}
        title="Link unavailable"
      >
        {label} ↗
      </span>
    )
  }
  return (
    <button
      type="button"
      class="ion-nav-link"
      title={artifact.filter_summary || label}
      onClick={() => applyNavLink(url, navigate)}
    >
      {label} ↗
    </button>
  )
}


export function MarkdownRenderer({
  content,
  sources = {},
  kpiGroups = [],
  toolCalls = [],
  className = '',
  // Ion Next admin page only: chips label derived / estimate / recommendation
  // values themselves (DataValueChip `provenance`). Off: the customer chip.
  chipProvenance = false,
  // Cross-message [[V:N]] resolver; undefined = chatStore.findValueByRef
  // (the customer conversation), unchanged for every customer surface.
  resolveRef,
}) {
  // planRenderSegments keeps text + non-ask_user markers in emission order but
  // hoists any ask_user dialog to the tail (so the lead-in prose always renders
  // above the question dialog). Text keys stay stable by original index.
  const segments = splitOnToolPlaceholders(String(content || ''))
  const plan = planRenderSegments(segments, toolCalls)

  // Reopen-from-history: the persisted assistant text carries NO [[TOOL:id]]
  // markers — they are injected client-side, live, by chatStore.startToolCall
  // and never round-trip to the backend (service.py accumulates only the
  // model's text). So on reopen there is no inline affordance for plots /
  // tables / ask_user dialogs, and they vanished from the chat (only the
  // canvas was reconstructed, fullpage-only). Render any renderable tool call
  // NOT already placed by a marker, after the prose, so they reappear on every
  // surface. Live, startToolCall places every id → this list is empty.
  // ToolMarkerSlot returns null for non-visual calls (search, and value chips
  // that render inline via [[V:N]]), so appending them is a no-op for those.
  const placedIds = new Set(
    segments.filter((s) => typeof s !== 'string').map((s) => s.toolId),
  )
  const orphanCalls = (toolCalls || []).filter(
    (tc) => tc && tc.id && !placedIds.has(tc.id),
  )

  return (
    <div class={`ion-md ${className}`.trim()}>
      {plan.map((item) =>
        item.kind === 'text' ? (
          <TextSegment
            key={item.key}
            content={item.content}
            sources={sources}
            kpiGroups={kpiGroups}
            toolCalls={toolCalls}
            chipProvenance={chipProvenance}
            resolveRef={resolveRef}
          />
        ) : (
          <ToolMarkerSlot key={item.key} toolId={item.toolId} toolCalls={toolCalls} />
        )
      )}
      {orphanCalls.map((tc) => (
        <ToolMarkerSlot
          key={`orphan-${tc.id}`}
          toolId={tc.id}
          toolCalls={toolCalls}
        />
      ))}
    </div>
  )
}
